import React, {useEffect, useState, useRef} from 'react'
import {observer} from 'mobx-react-lite'
import {Container, Stack, Text, Flex} from '../../vanilla'
import { ProductTile } from '../product-tile'
import {Carousel} from '../carousel'
import {Hit} from '../../store/ProductStore'
import { useProductStore, useGlobalStore, useBasketStore } from '../../store/hooks/useStore'
import { useHitsProductBadges } from '../algolia/util/useHitsProductBadges'
import { useViewItemListTracking } from '../../analytics/view-item-list-tracking'
import { useIntersectionObserver } from '../../hooks/use-intersection-observer'  
import * as styles from '../recommendations/styles.css'
import { ManualProductCarouselProps } from '../../types/cms'
import { transformHits } from '../algolia/util/transformHit'


const ManualProductCarouselContainer = ({content
}: ManualProductCarouselProps) => {
  
  const {title, productList} = content
  const productStore = useProductStore()
  const {selectedRegion, selectedPriceDayOffset, selectedStoreId} = useGlobalStore()
  const [fetchedProducts, setFetchedProducts] = useState<Hit[]>([])

  // Create an array of the productIds
  const productIds = productList?.products?.map((product: { id: string }) => product.id) || []
  
  // Fetch products on component load
  useEffect(() => {
    const fetchProducts = async () => {
      if (Array.isArray(productIds) && productIds.length > 0) {
        await productStore.fetchProducts(productIds, false)

        // Create an array of products and filter out undefined products
        const products = productIds
          .map((id) => productStore.productsById[id])
          .filter((product) => product !== undefined)
          // Only show 10 products
          .slice(0, 10)
          
        setFetchedProducts(transformHits(products as unknown as Hit[], selectedPriceDayOffset, selectedStoreId, selectedRegion))
      }
    }
    fetchProducts()
  }, [])

  // Fetch badges for the products
  useHitsProductBadges(fetchedProducts)


// GTM Tracking
  const sendViewItemListData = useViewItemListTracking()
  const basketStore = useBasketStore()
  const ref = useRef(null)
    const { isOnScreen, observerKey } = useIntersectionObserver(ref, {
      useOnce: true,
      /* Offset for intersection track. Sometimes a user can view the top of the product list section on a PDP,
      but the actual products are still not visible, so adding an offset to ensure that the event only fires when the user has scrolled to and viewed the product items,
       only then change observer state. */
      rootMargin: '-150px 0px -150px 0px',
    })

  // Send event to data layer when user scrolls to product carousel
    useEffect(() => {
      if (isOnScreen && fetchedProducts.length && basketStore.status !== 'init' && basketStore.status !== 'pending') {
        const viewItemListData = {
          items: fetchedProducts,
          listType: 
            {
              id: 'Manual-Product-Carousel',
              name: 'Manual Product Carousel'
            },
          pageId: null,
          region: selectedRegion
        }
        sendViewItemListData(viewItemListData)
      }
    }, [fetchedProducts, isOnScreen])

  return (
    <Container paddingX={['0px', '32px']} position="relative" ref={ref} key={observerKey}>
      <Stack gap={['20px', '28px']} position="relative">
        <Flex alignItems='center'>
          {title && (
            <Text 
              style={{
                width: '100%',
                textAlign: 'center',
                borderBottom: '1px solid #d8d8d8',
                lineHeight: '0px',
                margin: '20px 0px',
              }}>
              <span className={styles.recommenderTitle}>
                {title}
              </span>
            </Text>
          )}
        </Flex>
        <Carousel
          loop={false}
          className="recommendations-carousel"
          itemClassName={styles.recommenderItemSpacing}
          controlsPosition="bottom"
          mobilePeekMode={true}
          peekItemMaxWidth="clamp(220px, 55%, 260px)"
          controlConfig={{
            activeDotColor: 'gray800',
            showArrows: { mobile: false, desktop: true, tablet: true },
            arrowsColor: 'gray800',
          }}
          customResponsiveConfig={{
            desktop: {
              breakpoint: { min: 1024, max: 99999 },
              items: 5,
              slidesToSlide: 5,
            },
            tablet: {
              breakpoint: { max: 1024, min: 780 },
              items: 4,
              slidesToSlide: 4,
            },
            mobile: {
              breakpoint: { max: 780, min: 480 },
              items: 2,
              slidesToSlide: 2,
            },
            'mobile-xs': {
              breakpoint: { max: 480, min: 0 },
              items: 1,
              slidesToSlide: 1,
              partialVisibilityGutter: 50,
            },
          }}
        >
          {Array.isArray(fetchedProducts) && fetchedProducts.length > 0 && fetchedProducts?.map((product: any, hitItemIndex) => {
            return (
              product && (
                <ProductTile
                  hit={product as Hit}
                  isPLPTile={true}
                  key={product.id}
                  className={styles.recommenderHit}
                  hitItemIndex={hitItemIndex}
                />
              )
            )
          })}
        </Carousel>
      </Stack>
    </Container>
  )
}
export const ManualProductCarousel = observer(ManualProductCarouselContainer)